import { useEffect, useState } from "react"

/**
 * 브라우저 geolocation으로 현재 위치를 1회 조회.
 * 반복 호출을 피하기 위해 localStorage에 짧게(기본 1일) 캐시.
 *
 * 사용자 흐름:
 *  - 캐시 히트 → 즉시 coords 반환, status="granted"
 *  - 캐시 미스 → geolocation 프롬프트 (또는 이미 권한 있으면 조용히)
 *  - 거부/실패 → status="denied" | "unavailable" | "error", coords=null
 */

const CACHE_KEY = "user-location-cache"
const DEFAULT_TTL_MS = 1000 * 60 * 60 * 24 // 1일

export type GeolocationStatus = "idle" | "loading" | "granted" | "denied" | "unavailable" | "error"

export interface Coords {
  latitude: number
  longitude: number
}

export interface UseGeolocationResult {
  coords: Coords | null
  status: GeolocationStatus
  error: string | null
  /** 캐시 무시하고 새로 요청 */
  refresh: () => void
}

interface CacheEntry {
  ts: number
  coords: Coords
}

function readCache(ttlMs: number): Coords | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw) as CacheEntry
    if (Date.now() - entry.ts > ttlMs) {
      localStorage.removeItem(CACHE_KEY)
      return null
    }
    return entry.coords
  } catch {
    return null
  }
}

function writeCache(coords: Coords): void {
  try {
    const entry: CacheEntry = { ts: Date.now(), coords }
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    // quota 무시
  }
}

export interface UseGeolocationParams {
  /** 캐시 TTL (ms). 기본 1일. */
  ttlMs?: number
  /** 정확도 높은 위치 요청 (배터리 소모 ↑, 실내 정확도 ↑) */
  highAccuracy?: boolean
  /** 위치 요청 타임아웃 (ms). 기본 10초. */
  timeoutMs?: number
  /** 훅 활성화 여부 (로그인 후 켜기 등) */
  enabled?: boolean
}

export function useGeolocation(params: UseGeolocationParams = {}): UseGeolocationResult {
  const {
    ttlMs = DEFAULT_TTL_MS,
    highAccuracy = false,
    timeoutMs = 10_000,
    enabled = true,
  } = params

  const [coords, setCoords] = useState<Coords | null>(null)
  const [status, setStatus] = useState<GeolocationStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const [forceRefreshKey, setForceRefreshKey] = useState(0)

  useEffect(() => {
    if (!enabled) return

    // 1) 캐시 히트
    if (forceRefreshKey === 0) {
      const cached = readCache(ttlMs)
      if (cached) {
        setCoords(cached)
        setStatus("granted")
        return
      }
    }

    // 2) API 미지원 체크
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setStatus("unavailable")
      setError("Geolocation API is not available")
      return
    }

    // 3) 위치 요청
    setStatus("loading")
    setError(null)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next: Coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }
        setCoords(next)
        setStatus("granted")
        writeCache(next)
      },
      (err) => {
        setCoords(null)
        if (err.code === err.PERMISSION_DENIED) {
          setStatus("denied")
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setStatus("unavailable")
        } else {
          setStatus("error")
        }
        setError(err.message)
      },
      {
        enableHighAccuracy: highAccuracy,
        timeout: timeoutMs,
        maximumAge: ttlMs,
      },
    )
  }, [enabled, ttlMs, highAccuracy, timeoutMs, forceRefreshKey])

  const refresh = () => {
    try {
      localStorage.removeItem(CACHE_KEY)
    } catch {
      // 무시
    }
    setForceRefreshKey((k) => k + 1)
  }

  return { coords, status, error, refresh }
}
